import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { WorkshopMultiDetailDTO, WorkshopPreviewDTO } from "../../../../services/v2/workshops.service";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import S from "./strings";
import { Opciones, PromptModal, errorText, numOrNull, useRolDinero } from "./comun";
import { horasTexto } from "./dias";
import { Chip, ChipEstado, QueVaAPasar, Ventana, diasTexto, fechaDMY, hoyYmd, monto, rangoHoras } from "./piezasTaller";
import { DiaLista, Marca, PasarLista, RejillaAsistencia, marcaDe } from "./asistencia";
import { AgregarDias, DiasTaller } from "./sesiones";
import { Inscribir, Invitar } from "./inscribirInvitar";
import WorkshopForm from "./workshopForm";

/*
	The workshop window (Módulos Claros, 2026-11-01), opened from a row of the list. First «Inscritas y Asistencia» (the
	grid student x day is the only way to take attendance; on the phone, «Pasar Lista» opens today's list as a window), then
	«Días», then «Pago a Maestras» only when a teacher has her pay set up. History in one line and the buttons below.
*/

type Attendee = WorkshopMultiDetailDTO["attendees"][number];

const Dato: React.FC<{ t: string; children: React.ReactNode }> = ({ t, children }) => <div className="wtlDato"><small>{t}</small><div>{children}</div></div>;

/** What the student owes: "Falta L 900.00" in gold, "Pagado", or "Sin Cobro". */
export const Deuda: React.FC<{ a: Attendee; cur: any }> = ({ a, cur }) => {
	if (!a.charge || a.charge.statusId === 4) return <Chip>{a.paid ? S.pagado : S.sinCobro}</Chip>;
	return a.paid || a.charge.pending <= 0 ? <Chip>{S.pagado}</Chip> : <Chip tono="oro">{S.falta(monto(cur, a.charge.pending))}</Chip>;
};

/** «Pago a Maestras»: hours, rate and amount per teacher; the workshop's own hourly rate is edited here. */
const PagoMaestras: React.FC<{ campusId: number; t: WorkshopMultiDetailDTO; editable: boolean; onChange: (t: WorkshopMultiDetailDTO) => void; notify: (x: string, ok?: boolean) => void }> =
	({ campusId, t, editable, onChange, notify }) => {
		const http = HttpManager.getInstance();
		const p = t.mentorPay;
		const [tarifa, setTarifa] = useState<string>(t.mentorHourlyRate != null ? String(t.mentorHourlyRate) : "");
		const [busy, setBusy] = useState<boolean>(false);
		if (!p || !p.configured || p.rows.length === 0) return null;
		const tipo = (x: string | null) => x === "planta" ? S.planta : x === "hora" ? S.porHora : S.sinForma;
		const cambio = (tarifa.trim() === "" ? null : Number(tarifa)) !== (t.mentorHourlyRate ?? null);
		const guardar = () => {
			setBusy(true);
			http.workshopsService.update(campusId, { id: t.id, mentorHourlyRate: numOrNull(tarifa) } as any)
				.then((r) => { onChange(r.data as WorkshopMultiDetailDTO); notify(S.guardado); })
				.catch((e) => notify(errorText(e), false))
				.finally(() => setBusy(false));
		};
		return (
			<div className="wtlSeccion">
				<div className="wtlSecCab">
					<h6>{S.pagoMaestras}</h6>
					{editable ? (
						<span style={{ display: "inline-flex", gap: 6, alignItems: "flex-end" }}>
							<label className="wau-campo" style={{ width: 160 }}><span className="wau-lab">{S.tarifaTaller}</span>
								<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={tarifa} onChange={(e) => setTarifa(e.target.value)} /></label>
							{cambio ? <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={guardar}>{S.guardar}</button> : null}
						</span>
					) : null}
				</div>
				<table className="table table-sm wtlTabla">
					<thead><tr><th>{S.maestra}</th><th>{S.formaPago}</th><th className="wtlNum">{S.horas}</th><th className="wtlNum">{S.tarifa}</th><th className="wtlNum">{S.aPagar}</th></tr></thead>
					<tbody>
						{p.rows.map((r) => (
							<tr key={r.mentorId}>
								<td><b>{r.name}</b>{r.amount === null ? <div className="wtlSub">{r.note}</div> : null}</td>
								<td>{p.hourlyRateOverride !== null ? S.tarifaTaller : tipo(r.payType)}</td>
								<td className="wtlNum">{horasTexto(r.hours)}</td>
								<td className="wtlNum">{r.rate !== null ? monto(t.currency, r.rate) : "—"}</td>
								<td className="wtlNum">{r.amount !== null ? monto(t.currency, r.amount) : <Chip tono="oro">{S.sinForma}</Chip>}</td>
							</tr>
						))}
					</tbody>
					{p.rows.length > 1 ? <tfoot><tr><td colSpan={4}><b>{S.total}</b></td><td className="wtlNum"><b>{monto(t.currency, p.total)}</b></td></tr></tfoot> : null}
				</table>
			</div>
		);
	};

const TallerVentana: React.FC<{ campusId: number; id: number; opciones: Opciones; onClose: () => void; onChanged: () => void; notify: (text: string, ok?: boolean) => void }> =
	({ campusId, id, opciones, onClose, onChanged, notify }) => {
		const http = HttpManager.getInstance();
		const movil = useEsTelefono();
		const rolDinero = useRolDinero(campusId);
		const [t, setT] = useState<WorkshopMultiDetailDTO | null>(null);
		const [error, setError] = useState<string | null>(null);
		const [aviso, setAviso] = useState<{ text: string; ok: boolean } | null>(null);
		const [enCurso, setEnCurso] = useState<string[]>([]);
		const [abierto, setAbierto] = useState<null | "inscribir" | "lista" | "invitar" | "editar">(null);
		const [editandoDias, setEditandoDias] = useState<boolean>(false);
		const [agregandoDias, setAgregandoDias] = useState<boolean>(false);
		const peticion = useRef<number>(0); // only the answer to the latest mark is painted
		const [anular, setAnular] = useState<WorkshopPreviewDTO | null>(null);
		const [baja, setBaja] = useState<Attendee | null>(null);
		const [busy, setBusy] = useState<boolean>(false);
		const [errorAccion, setErrorAccion] = useState<string | null>(null);

		const avisar = (text: string, ok = true) => { setAviso(text ? { text, ok } : null); if (ok && text) onChanged(); };
		const cargar = () => http.workshopsService.details(campusId, id).then((r) => setT(r.data as WorkshopMultiDetailDTO)).catch((e) => setError(errorText(e)));
		useEffect(() => { cargar();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [campusId, id]);

		const dias: DiaLista[] = useMemo(() => (t?.sessions || []).map((x) => ({ id: x.id, date: x.date, startTime: x.startTime, endTime: x.endTime })), [t]);
		const marcas = (a: Attendee): Record<number, Marca> => {
			const m: Record<number, Marca> = {};
			(a.sessions || []).forEach((s) => { m[s.sessionId] = marcaDe(s.attended, s.marked); });
			return m;
		};

		if (!t) {
			return <Ventana titulo={S.cargando} onClose={onClose}>{error ? <div className="waAviso" role="alert">{error}</div> : <div className="loader" />}</Ventana>;
		}

		const soloLectura = t.isHistoric || t.status === "anulado";
		const vigentes = t.attendees.filter((a) => a.status === 1);
		const deBaja = t.attendees.filter((a) => a.status !== 1);
		const porSolicitud = vigentes.filter((a) => a.requestId).length;
		const hoy = hoyYmd();
		const ultimaLista = [...(t.sessions || [])].reverse().find((x) => (x.attendance.marked || 0) > 0 || x.attendance.attended > 0);
		const cursos = (t.courses || []).map((c) => cursoCorto(c.name) || c.name).join(", ") || cursoCorto(t.courseName) || t.area || "";
		const titulo = t.isPersonalizado && t.studentName ? t.studentName : t.name;

		const marcar = (diaId: number, attendeeId: number, vino: boolean) => {
			const clave = diaId + "_" + attendeeId;
			setErrorAccion(null);
			setEnCurso((x) => [...x, clave]);
			setT((x) => x ? { ...x, attendees: x.attendees.map((a) => a.id !== attendeeId ? a : { ...a, sessions: (a.sessions || []).map((s) => s.sessionId === diaId ? { ...s, attended: vino, marked: true } : s) }) } : x);
			const n = ++peticion.current;
			http.workshopsService.setSessionAttendance(campusId, diaId, attendeeId, vino)
				.then((r) => { if (n === peticion.current) setT(r.data); })
				.catch((e) => { setErrorAccion(errorText(e)); cargar(); })
				.finally(() => setEnCurso((x) => x.filter((y) => y !== clave)));
		};
		const pedirAnular = () => {
			setBusy(true); setErrorAccion(null);
			http.workshopsService.preview(campusId, t.id, "anular").then((r) => setAnular(r.data)).catch((e) => setAviso({ text: errorText(e), ok: false })).finally(() => setBusy(false));
		};
		const confirmarAnular = () => {
			setBusy(true); setErrorAccion(null);
			http.workshopsService.cancel(campusId, t.id)
				.then((r) => { setT(r.data); setAnular(null); avisar(S.anulado); })
				.catch((e) => setErrorAccion(errorText(e)))
				.finally(() => setBusy(false));
		};

		const nombreAlumna = (a: Attendee) => <>
			<b>{a.studentName}</b>
			<div className="wtlSub">{[a.identityCode, a.soldBy ? S.vendio + " " + a.soldBy : "", a.requestId ? "#" + a.requestId : "", a.status !== 1 ? S.deBaja : ""].filter(Boolean).join(", ")}</div>
		</>;
		const finFila = (a: Attendee) => (
			<span style={{ display: "inline-flex", gap: 6, alignItems: "center", justifyContent: "flex-end" }}>
				<Deuda a={a} cur={t.currency} />
				{!soloLectura && a.status === 1 ? <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, color: "var(--wa-apagado)" }} onClick={() => setBaja(a)}>{S.baja}</button> : null}
			</span>
		);
		const vinoDe = (a: Attendee) => (a.sessions || []).filter((s) => s.attended).length;

		const pie = soloLectura ? null : <>
			<button type="button" className="btn btn-primary btn-sm" onClick={() => setAbierto("inscribir")} disabled={t.spotsLeft === 0}>{S.inscribir}</button>
			<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAbierto("lista")} disabled={dias.length === 0 || vigentes.length === 0}>{S.pasarLista}</button>
			<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setAbierto("invitar")}>{S.invitar}</button>
			<button type="button" className={"btn btn-sm " + (editandoDias ? "btn-secondary" : "btn-outline-secondary")} onClick={() => setEditandoDias((x) => !x)}>{editandoDias ? S.listo : S.editarDias}</button>
			<span style={{ flex: 1 }} />
			<button type="button" className="btn btn-outline-secondary btn-sm" onClick={pedirAnular} disabled={busy}>{S.anular}</button>
		</>;

		return (
			<>
				<Ventana size="xl" onClose={onClose} pie={pie} titulo={<>
					<span>{titulo}</span>
					<ChipEstado tab={t.tab} />
					{!soloLectura ? <button type="button" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 999, padding: "2px 12px" }} onClick={() => setAbierto("editar")}>{S.editar}</button> : null}
				</>}>
					{aviso ? <div className={aviso.ok ? "wtlAviso" : "waAviso"} role="status" style={{ display: "flex", justifyContent: "space-between", marginTop: 0, marginBottom: 10 }}>
						{aviso.text}<button type="button" className="btn-close" style={{ fontSize: 9 }} aria-label={S.cerrar} onClick={() => setAviso(null)} /></div> : null}
					<div className="wtlDatos">
						{t.isPersonalizado && t.studentName ? <Dato t={S.col.taller}>{t.name}</Dato> : null}
						<Dato t={S.dias}>{diasTexto(t.sessionDates || [])}<div className="wtlSub">{S.nDias(dias.length)}</div></Dato>
						<Dato t={S.horario}>{t.sameTime === false ? S.segunDia : rangoHoras(t.timeFrom, t.timeTo) || "—"}</Dato>
						{cursos ? <Dato t={S.cursos}>{cursos}</Dato> : null}
						<Dato t={S.precio}><span className="wau-num">{monto(t.currency, t.price)}</span>{t.priceStudent ? <div className="wtlSub">{S.precioMatriculada} {monto(t.currency, t.priceStudent)}</div> : null}</Dato>
						<Dato t={S.inscritasN}><span className={"wau-num" + (t.spotsLeft === 0 ? " wtlOro" : "")}>{vigentes.length}{t.capacity ? " / " + t.capacity : ""}</span></Dato>
						<Dato t={S.cobrado}><span className="wau-num">{monto(t.currency, t.collected || 0)}</span>{(t.pending || 0) > 0 ? <div className="wtlOro wau-num" style={{ fontSize: 13 }}>{S.falta(monto(t.currency, t.pending))}</div> : null}</Dato>
					</div>
					{t.notes ? <div className="wtlSub" style={{ marginTop: 8 }}>{t.notes}</div> : null}

					<div className="wtlSeccion">
						<div className="wtlSecCab"><h6>{S.inscritasAsistencia} <span className="wtlSub" style={{ display: "inline" }}>{vigentes.length}</span></h6></div>
						{t.attendees.length === 0 ? <div className="wtlSub">{S.sinInscritas}</div> : movil || dias.length === 0 ? (
							<div>
								{dias.length === 0 && !soloLectura ? <button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ marginBottom: 8 }} onClick={() => setAgregandoDias(true)}>{S.agregarDias}</button> : null}
								{[...vigentes, ...deBaja].map((a) => (
									<div key={a.id} className={a.status !== 1 ? "wtlBaja" : ""} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--wa-filo)", alignItems: "center" }}>
										<div style={{ minWidth: 0 }}>{nombreAlumna(a)}{dias.length ? <div className="wtlSub">{S.vino} {S.presentes(vinoDe(a), dias.filter((d) => d.date <= hoy).length)}</div> : null}</div>
										{finFila(a)}
									</div>
								))}
							</div>
						) : (
							<RejillaAsistencia dias={dias} editable={!soloLectura} enCurso={enCurso} finTitulo="" onMarcar={marcar}
								filas={[...vigentes, ...deBaja].map((a) => ({ attendeeId: a.id, name: nombreAlumna(a), activa: a.status === 1, marcas: marcas(a), fin: finFila(a) }))} />
						)}
						{errorAccion && !anular ? <div className="waAviso" role="alert">{errorAccion}</div> : null}
					</div>

					<DiasTaller campusId={campusId} taller={t} opciones={opciones} editando={editandoDias && !soloLectura} onChange={setT} notify={avisar} />

					{rolDinero ? <PagoMaestras campusId={campusId} t={t} editable={!soloLectura} onChange={setT} notify={avisar} /> : null}

					<div className="wtlHistoria">
						{t.createdAt ? <span><b>{S.creadoPor}</b> {fechaDMY(t.createdAt)}{t.createdByName ? " " + t.createdByName : ""}</span> : null}
						<span><b>{S.inscritasN}</b> {vigentes.length}{porSolicitud ? " (" + S.porSolicitud(porSolicitud) + ")" : ""}{deBaja.length ? ", " + deBaja.length + " " + S.deBaja : ""}</span>
						{ultimaLista ? <span><b>{S.ultimaLista}</b> {fechaDMY(ultimaLista.date)}</span> : null}
					</div>
				</Ventana>

				{agregandoDias ? <AgregarDias campusId={campusId} taller={t} opciones={opciones} onClose={() => setAgregandoDias(false)}
					onSaved={(x, n) => { setT(x); setAgregandoDias(false); avisar(S.diasAgregados(n)); }} /> : null}
				{abierto === "lista" ? (
					<PasarLista titulo={<>{S.pasarLista}<span className="wtlSub" style={{ fontSize: 14 }}>{titulo}</span></>} dias={dias} enCurso={enCurso} error={errorAccion}
						onMarcar={marcar} onClose={() => setAbierto(null)}
						alumnas={(diaId) => vigentes.map((a) => { const s = (a.sessions || []).find((x) => x.sessionId === diaId); return { attendeeId: a.id, name: a.studentName, code: a.identityCode, marca: marcaDe(s?.attended, s?.marked) }; })} />
				) : null}
				{abierto === "inscribir" ? <Inscribir campusId={campusId} taller={t} rolDinero={rolDinero} onClose={() => setAbierto(null)}
					onDone={(x, texto) => { setT(x); setAbierto(null); avisar(texto); }} /> : null}
				{abierto === "invitar" ? <Invitar campusId={campusId} taller={t} opciones={opciones} onClose={() => setAbierto(null)}
					onInscritas={(texto, ok) => { avisar(texto, ok); cargar(); }} /> : null}
				{abierto === "editar" ? <WorkshopForm campusId={campusId} opciones={opciones} taller={t as any} onClose={() => setAbierto(null)}
					onSaved={(x, texto) => { setT(x as WorkshopMultiDetailDTO); setAbierto(null); avisar(texto); }} /> : null}
				{anular ? <QueVaAPasar vista={anular} boton={S.anularTaller} busy={busy} error={errorAccion} bloqueaTodo onCancel={() => { setAnular(null); setErrorAccion(null); }} onConfirm={confirmarAnular} /> : null}
				{baja ? (
					<PromptModal title={S.bajaTitulo(baja.studentName)} label={S.bajaNota} confirm={S.baja} optional busy={busy} onCancel={() => setBaja(null)}
						onConfirm={(nota) => {
							setBusy(true);
							http.workshopsService.removeAttendee(campusId, baja.id, nota || undefined)
								.then((r) => { setT(r.data as WorkshopMultiDetailDTO); setBaja(null); avisar(S.bajaOk); })
								.catch((e) => { setBaja(null); setAviso({ text: errorText(e), ok: false }); })
								.finally(() => setBusy(false));
						}}>
						<div className="wau-efectos" style={{ marginBottom: 10, fontSize: 14 }}>
							{!baja.charge || baja.charge.statusId === 4 ? <div>{S.sinCobro}</div>
								: baja.charge.paid > 0 ? <div className="wau-oro">Ya abonó {monto(t.currency, baja.charge.paid)}: corrige ese pago en Caja antes de darla de baja</div>
								: <div>Se anula su cobro de {monto(t.currency, baja.charge.total)}</div>}
						</div>
					</PromptModal>
				) : null}
			</>
		);
	};

export default TallerVentana;
